/**
 * BuchhaltungPage — Rechnungsverwaltung + DATEV Export + Zahlungseingänge
 * DESIGN: Reduziert, professionell. Keine bunten Farben.
 * 
 * Features:
 * - Rechnungsübersicht mit Betrieb-Filter (8 Betriebe)
 * - Neue Rechnung erstellen (Dialog mit Positionen)
 * - DATEV CSV Export
 * - Zahlungseingänge erfassen
 * - Honorar-Rechner (Gutachten)
 * - PDF-Vorschau
 *
 * EMBEDDED-MODUS (Feature buchhaltung-ein-ort):
 * - Über die optionalen Props `embedded` und `lockBusinessUnit` lässt sich die
 *   Rechnungs-Liste (inkl. KPIs Gesamt/Offen/Bezahlt/Überfällig) in der
 *   konsolidierten Buchhaltung pro Firma wiederverwenden — ohne Logik-Dopplung.
 * - Ohne Props verhält sich die Seite EXAKT wie bisher (Route /buchhaltung/rechnungen).
 *
 * FINANZEN-COCKPIT-V2 (Feature finanzen-cockpit-v2):
 * - Filterleiste um Monat (aus rechnungsdatum, YYYY-MM) und Status (inkl.
 *   abgeleitetem "Überfällig") erweitert.
 * - Neue Aktion "Ansehen" öffnet die hinterlegte pdf_url in einem Modal (iframe);
 *   das bestehende Drucken/PDF-Generieren bleibt zusätzlich erhalten.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useRechnungen, useBetriebe, useHonorartabelle, useDatevExporte, useLeistungsKatalog } from '@/hooks/useSupabase';
import { RECHNUNGS_STATUS_COLORS, type BusinessUnit } from '@/lib/supabase';
import { BETRIEBE, BETRIEB_LIST, FEATURE_STATUS, type BetriebId } from '@/lib/betriebe';
import { supabase } from '@/lib/supabase';
import { generiereRechnungsnummer, berechneBetraege, generiereRechnungHTML, generiereeDatevCSV, rechnungenZuDatevBuchungen, formatEuro, formatDatum, DATEV_KONTEN } from '@/lib/rechnungen';
import type { Rechnung, Rechnungsposition, BetriebDB } from '@/lib/database.types';
import {
  Receipt, Plus, Filter, Download, FileText, X, Trash2,
  CreditCard, TrendingUp, AlertCircle, CheckCircle2,
  Eye, Edit2, Printer, Calculator, ChevronDown, ChevronRight,
  Calendar, Building2, Search, MoreHorizontal, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

const RSTATUS_LABELS: Record<string, string> = {
  entwurf: 'Entwurf', erstellt: 'Erstellt', versendet: 'Versendet', gesendet: 'Gesendet',
  bezahlt: 'Bezahlt', teilbezahlt: 'Teilbezahlt', kuerzung: 'Kürzung',
  ueberfaellig: 'Überfällig', storniert: 'Storniert', mahnung: 'Mahnung',
};

const RSTATUS_COLORS: Record<string, string> = {
  entwurf: 'bg-gray-100 text-gray-500',
  erstellt: 'bg-blue-50 text-blue-600',
  versendet: 'bg-blue-50 text-blue-600',
  gesendet: 'bg-blue-50 text-blue-600',
  bezahlt: 'bg-emerald-50 text-emerald-600',
  teilbezahlt: 'bg-amber-50 text-amber-600',
  kuerzung: 'bg-orange-50 text-orange-600',
  ueberfaellig: 'bg-red-50 text-red-600',
  storniert: 'bg-gray-50 text-gray-400',
  mahnung: 'bg-red-50 text-red-700',
};

/** Status-Optionen für den Status-Filter der Rechnungs-Liste.
 *  "ueberfaellig" wird zusätzlich client-seitig abgeleitet (offen & faellig_am < heute). */
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'alle', label: 'Alle Status' },
  { value: 'offen', label: 'Offen' },
  { value: 'bezahlt', label: 'Bezahlt' },
  { value: 'ueberfaellig', label: 'Überfällig' },
];

/** Gilt eine Rechnung als "offen" (noch nicht bezahlt/storniert)? */
function istOffen(status: string): boolean {
  return ['entwurf', 'erstellt', 'versendet', 'gesendet', 'teilbezahlt', 'mahnung', 'ueberfaellig'].includes(status);
}

/** Ist eine Rechnung überfällig? (offen UND faellig_am < heute) */
function istUeberfaellig(r: Pick<Rechnung, 'status' | 'faellig_am'>): boolean {
  if (r.status === 'ueberfaellig' || r.status === 'mahnung') return true;
  if (!r.faellig_am) return false;
  if (!istOffen(r.status)) return false;
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  return new Date(r.faellig_am) < heute;
}

// ── PDF-Ansehen Modal ──
// Zeigt eine hinterlegte Rechnungs-PDF (pdf_url) in einem iframe an.
function PdfViewerDialog({ open, url, titel, onClose }: { open: boolean; url: string | null; titel?: string; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <h2 className="text-sm font-bold truncate">{titel || 'Rechnung'}</h2>
          </div>
          <div className="flex items-center gap-1">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-gray-100">
          {url ? (
            <iframe src={url} title={titel || 'Rechnung'} className="w-full h-full border-0" />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-400">
              Keine PDF hinterlegt
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Rechnungs-Vorschau (Slide-over von rechts) ──
// Zeigt die ausgewählte Rechnung als Panel von der rechten Bildschirmseite (ca. 55% Breite,
// auf Mobile volle Breite). Kopf mit Nr./Empfänger/Betrag/Status + Download als sekundäre
// Aktion. Darunter die PDF-Vorschau (pdf_url) im iframe. Schließbar via X, Overlay-Klick, Esc.
function RechnungVorschauPanel({
  rechnung,
  onClose,
  onDrucken,
}: {
  rechnung: any | null;
  onClose: () => void;
  onDrucken: (r: any) => void;
}) {
  const open = !!rechnung;

  // Schließen via Esc-Taste + Body-Scroll sperren, solange das Panel offen ist.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!rechnung) return null;

  const ueberfaellig = istUeberfaellig(rechnung);
  const anzeigeStatus = ueberfaellig && rechnung.status !== 'ueberfaellig' && rechnung.status !== 'mahnung'
    ? 'ueberfaellig'
    : rechnung.status;
  const empfaenger = rechnung.empfaenger_name
    || (rechnung.kunden ? `${rechnung.kunden.nachname}, ${rechnung.kunden.vorname}` : '-');

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Halbtransparentes Overlay (links daneben) — Klick schließt */}
      <div
        className="flex-1 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel von rechts */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Vorschau Rechnung ${rechnung.rechnungs_nr || ''}`}
        className="relative w-full sm:w-[55%] sm:min-w-[50%] h-full bg-white shadow-2xl flex flex-col"
      >
        {/* Kopf */}
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-semibold text-gray-500">{rechnung.rechnungs_nr}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${RSTATUS_COLORS[anzeigeStatus] || 'bg-gray-100 text-gray-500'}`}>
                {RSTATUS_LABELS[anzeigeStatus] || anzeigeStatus}
              </span>
            </div>
            <h2 className="text-sm font-bold truncate mt-1">{rechnung.betreff || 'Rechnung'}</h2>
            <p className="text-[11px] text-muted-foreground truncate">{empfaenger}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Betrag</p>
              <p className="text-base font-bold">{formatEuro(rechnung.brutto_betrag)}</p>
            </div>
            {rechnung.pdf_url && (
              <a
                href={rechnung.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-border hover:bg-gray-100 transition-colors"
                title="Download"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Schließen" aria-label="Schließen">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* PDF-Vorschau (Resthöhe) */}
        <div className="flex-1 min-h-0 bg-gray-100">
          {rechnung.pdf_url ? (
            <iframe src={rechnung.pdf_url} title={rechnung.rechnungs_nr || 'Rechnung'} className="w-full h-full border-0" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <FileText className="w-8 h-8 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Keine PDF hinterlegt</p>
              <button
                onClick={() => onDrucken(rechnung)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
              >
                <Printer className="w-3.5 h-3.5" /> Vorschau erzeugen / Drucken
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Neue Rechnung Dialog ──
interface NeueRechnungForm {
  business_unit: string;
  betreff: string;
  empfaenger_name: string;
  empfaenger_firma: string;
  empfaenger_strasse: string;
  empfaenger_plz: string;
  empfaenger_ort: string;
  mwst_satz: number;
  kennzeichen: string;
  schadensnummer: string;
  versicherung: string;
  fahrzeug: string;
  anwalt_beteiligt: boolean;
  anwalt_name: string;
  positionen: { bezeichnung: string; beschreibung: string; menge: number; einheit: string; einzelpreis: number; mwst_satz: number; katalog_id?: string }[];
}

const INITIAL_FORM: NeueRechnungForm = {
  business_unit: 'gutachten',
  betreff: '',
  empfaenger_name: '',
  empfaenger_firma: '',
  empfaenger_strasse: '',
  empfaenger_plz: '',
  empfaenger_ort: '',
  mwst_satz: 19,
  kennzeichen: '',
  schadensnummer: '',
  versicherung: '',
  fahrzeug: '',
  anwalt_beteiligt: false,
  anwalt_name: '',
  positionen: [{ bezeichnung: '', beschreibung: '', menge: 1, einheit: 'Stück', einzelpreis: 0, mwst_satz: 19 }],
};

function NeueRechnungDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState<NeueRechnungForm>({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'empfaenger' | 'positionen' | 'details'>('empfaenger');

  const { data: leistungsKatalog } = useLeistungsKatalog(form.business_unit);

  const updateForm = (key: keyof NeueRechnungForm, value: any) => setForm(f => ({ ...f, [key]: value }));

  const addPosition = () => setForm(f => ({
    ...f,
    positionen: [...f.positionen, { bezeichnung: '', beschreibung: '', menge: 1, einheit: 'Stück', einzelpreis: 0, mwst_satz: 19 }],
  }));

  const removePosition = (idx: number) => setForm(f => ({
    ...f,
    positionen: f.positionen.filter((_, i) => i !== idx),
  }));

  const updatePosition = (idx: number, key: string, value: any) => setForm(f => ({
    ...f,
    positionen: f.positionen.map((p, i) => i === idx ? { ...p, [key]: value } : p),
  }));

  const totals = useMemo(() => {
    let netto = 0;
    let mwst = 0;
    for (const pos of form.positionen) {
      const posNetto = pos.menge * pos.einzelpreis;
      mwst += posNetto * (pos.mwst_satz / 100);
      netto += posNetto;
    }
    return { netto: Math.round(netto * 100) / 100, mwst: Math.round(mwst * 100) / 100, brutto: Math.round((netto + mwst) * 100) / 100 };
  }, [form.positionen]);

  const handleSave = async () => {
    if (!form.betreff || !form.empfaenger_name || form.positionen.length === 0) {
      toast.error('Bitte Betreff, Empfänger und mindestens eine Position ausfüllen');
      return;
    }
    setSaving(true);
    try {
      const rechnungsNr = await generiereRechnungsnummer(form.business_unit);
      const faelligAm = new Date();
      faelligAm.setDate(faelligAm.getDate() + 14);

      // Betrieb-ID für die Rechnung (aus betriebe-Tabelle nach typ)
      const { data: betriebRow, error: betriebError } = await supabase
        .from('betriebe')
        .select('id')
        .eq('typ', form.business_unit)
        .limit(1)
        .single();

      if (betriebError || !betriebRow) {
        throw new Error(`Kein Betrieb für Business-Unit "${form.business_unit}" gefunden — bitte Stammdaten prüfen`);
      }

      const { data: rechnung, error } = await supabase
        .from('rechnungen')
        .insert({
          rechnungs_nr: rechnungsNr,
          business_unit: form.business_unit,
          betrieb_id: betriebRow.id,
          betreff: form.betreff,
          empfaenger_name: form.empfaenger_name,
          empfaenger_firma: form.empfaenger_firma,
          empfaenger_strasse: form.empfaenger_strasse,
          empfaenger_plz: form.empfaenger_plz,
          empfaenger_ort: form.empfaenger_ort,
          netto_betrag: totals.netto,
          mwst_satz: form.mwst_satz,
          mwst_betrag: totals.mwst,
          brutto_betrag: totals.brutto,
          status: 'entwurf',
          rechnungsdatum: new Date().toISOString().split('T')[0],
          faellig_am: faelligAm.toISOString().split('T')[0],
          kennzeichen: form.kennzeichen || null,
          schadensnummer: form.schadensnummer || null,
          versicherung: form.versicherung || null,
          fahrzeug: form.fahrzeug || null,
          anwalt_beteiligt: form.anwalt_beteiligt,
          anwalt_name: form.anwalt_name || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Positionen speichern (inkl. katalog_id für DB-seitige Bereichsprüfung)
      if (rechnung) {
        const positionen = form.positionen.map((p, idx) => ({
          rechnung_id: rechnung.id,
          position_nr: idx + 1,
          bezeichnung: p.bezeichnung,
          beschreibung: p.beschreibung || null,
          menge: p.menge,
          einheit: p.einheit,
          einzelpreis_netto: p.einzelpreis,
          mwst_satz: p.mwst_satz,
          ...(p.katalog_id ? { katalog_id: p.katalog_id } : {}),
        }));

        const { error: posError } = await supabase.from('rechnungspositionen').insert(positionen);
        if (posError) throw posError;
      }

      toast.success(`Rechnung ${rechnungsNr} erstellt`);
      setForm({ ...INITIAL_FORM });
      onCreated();
      onClose();
    } catch (err: any) {
      toast.error(`Fehler: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-3xl md:h-auto max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold">Neue Rechnung</h2>
            <p className="text-xs text-gray-400">Rechnung erstellen und als PDF exportieren</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Betrieb + Betreff */}
        <div className="px-6 py-3 border-b border-gray-50 bg-gray-50/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Betrieb</label>
              <select
                value={form.business_unit}
                onChange={(e) => setForm(f => ({
                  ...f,
                  business_unit: e.target.value,
                  positionen: f.positionen.filter(p => !p.katalog_id),
                }))}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
              >
                {BETRIEB_LIST.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Betreff</label>
              <input
                value={form.betreff}
                onChange={(e) => updateForm('betreff', e.target.value)}
                placeholder="z.B. Gutachten Haftpflichtschaden"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {(['empfaenger', 'positionen', 'details'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                activeTab === tab ? 'border-[#9B1C1C] text-[#9B1C1C]' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab === 'empfaenger' ? 'Empfänger' : tab === 'positionen' ? 'Positionen' : 'Details'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === 'empfaenger' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Name</label>
                  <input value={form.empfaenger_name} onChange={(e) => updateForm('empfaenger_name', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Max Mustermann" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Firma</label>
                  <input value={form.empfaenger_firma} onChange={(e) => updateForm('empfaenger_firma', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Optional" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase">Straße</label>
                <input value={form.empfaenger_strasse} onChange={(e) => updateForm('empfaenger_strasse', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">PLZ</label>
                  <input value={form.empfaenger_plz} onChange={(e) => updateForm('empfaenger_plz', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Ort</label>
                  <input value={form.empfaenger_ort} onChange={(e) => updateForm('empfaenger_ort', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'positionen' && (
            <div className="space-y-3">
              {leistungsKatalog && leistungsKatalog.length > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50/60 border border-blue-100">
                  <span className="text-[10px] font-semibold text-blue-600">Katalog ({form.business_unit}):</span>
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      const item = leistungsKatalog.find(k => k.id === e.target.value);
                      if (item) {
                        setForm(f => ({
                          ...f,
                          positionen: [
                            ...f.positionen,
                            { bezeichnung: item.bezeichnung, beschreibung: '', menge: 1, einheit: item.einheit || 'Stück', einzelpreis: item.einzelpreis, mwst_satz: 19, katalog_id: item.id },
                          ],
                        }));
                        e.target.value = '';
                      }
                    }}
                    className="flex-1 px-2 py-1 rounded border border-blue-200 bg-white text-xs"
                  >
                    <option value="">Aus Katalog hinzufügen…</option>
                    {leistungsKatalog.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.bezeichnung}{item.einheit ? ` (${item.einheit})` : ''} — {formatEuro(item.einzelpreis)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {form.positionen.map((pos, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-gray-400">Position {idx + 1}</span>
                      {pos.katalog_id && (
                        <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-500 border border-blue-100">Katalog</span>
                      )}
                    </div>
                    {form.positionen.length > 1 && (
                      <button onClick={() => removePosition(idx)} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <input
                    value={pos.bezeichnung}
                    onChange={(e) => updatePosition(idx, 'bezeichnung', e.target.value)}
                    placeholder="Bezeichnung"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm mb-2"
                    list={`katalog-suggestions-${idx}`}
                  />
                  {leistungsKatalog && leistungsKatalog.length > 0 && (
                    <datalist id={`katalog-suggestions-${idx}`}>
                      {leistungsKatalog.map(item => (
                        <option key={item.id} value={item.bezeichnung} />
                      ))}
                    </datalist>
                  )}
                  <input
                    value={pos.beschreibung}
                    onChange={(e) => updatePosition(idx, 'beschreibung', e.target.value)}
                    placeholder="Beschreibung (optional)"
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-500 mb-2"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[9px] text-gray-400">Menge</label>
                      <input type="number" step="0.01" value={pos.menge} onChange={(e) => updatePosition(idx, 'menge', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm" />
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-400">Einheit</label>
                      <select value={pos.einheit} onChange={(e) => updatePosition(idx, 'einheit', e.target.value)} className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm">
                        <option value="Stück">Stück</option>
                        <option value="Stunden">Stunden</option>
                        <option value="km">km</option>
                        <option value="Tage">Tage</option>
                        <option value="Pauschal">Pauschal</option>
                        <option value="AW">AW</option>
                        <option value="Seite">Seite</option>
                        <option value="Tag">Tag</option>
                        <option value="%">%</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-400">Einzelpreis</label>
                      <input type="number" step="0.01" value={pos.einzelpreis} onChange={(e) => updatePosition(idx, 'einzelpreis', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm" />
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-400">MwSt.</label>
                      <select value={pos.mwst_satz} onChange={(e) => updatePosition(idx, 'mwst_satz', parseFloat(e.target.value))} className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm">
                        <option value="19">19%</option>
                        <option value="7">7%</option>
                        <option value="0">0%</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-right mt-1">
                    <span className="text-xs font-semibold text-gray-600">{formatEuro(pos.menge * pos.einzelpreis)}</span>
                  </div>
                </div>
              ))}
              <button onClick={addPosition} className="w-full py-2 rounded-lg border border-dashed border-gray-300 text-xs font-medium text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors">
                + Position hinzufügen
              </button>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Kennzeichen</label>
                  <input value={form.kennzeichen} onChange={(e) => updateForm('kennzeichen', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="MK-AB 1234" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Fahrzeug</label>
                  <input value={form.fahrzeug} onChange={(e) => updateForm('fahrzeug', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="BMW 320d" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Schadensnummer</label>
                  <input value={form.schadensnummer} onChange={(e) => updateForm('schadensnummer', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Versicherung</label>
                  <input value={form.versicherung} onChange={(e) => updateForm('versicherung', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-3 py-2">
                <input type="checkbox" checked={form.anwalt_beteiligt} onChange={(e) => updateForm('anwalt_beteiligt', e.target.checked)} className="rounded" />
                <label className="text-sm">Anwalt beteiligt</label>
              </div>
              {form.anwalt_beteiligt && (
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase">Anwalt Name</label>
                  <input value={form.anwalt_name} onChange={(e) => updateForm('anwalt_name', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer mit Summen */}
        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="flex gap-6 text-xs">
              <div><span className="text-gray-400">Netto:</span> <span className="font-semibold">{formatEuro(totals.netto)}</span></div>
              <div><span className="text-gray-400">MwSt.:</span> <span className="font-semibold">{formatEuro(totals.mwst)}</span></div>
              <div><span className="text-gray-400">Brutto:</span> <span className="font-bold text-base">{formatEuro(totals.brutto)}</span></div>
            </div>
            <div className="flex gap-2 mobile-dialog-actions sm:pb-0">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-100 transition-colors">
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-semibold hover:bg-[#7F1D1D] transition-colors disabled:opacity-50"
              >
                {saving ? 'Speichern...' : 'Rechnung erstellen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DATEV Export Dialog ──
function DatevExportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [zeitraumVon, setZeitraumVon] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [zeitraumBis, setZeitraumBis] = useState(() => new Date().toISOString().split('T')[0]);
  const [betriebFilter, setBetriebFilter] = useState('alle');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      let query = supabase
        .from('rechnungen')
        .select('*')
        .gte('rechnungsdatum', zeitraumVon)
        .lte('rechnungsdatum', zeitraumBis)
        .neq('status', 'entwurf')
        .neq('status', 'storniert');

      if (betriebFilter !== 'alle') {
        query = query.eq('business_unit', betriebFilter);
      }

      const { data: rechnungen, error } = await query;
      if (error) throw error;
      if (!rechnungen || rechnungen.length === 0) {
        toast.error('Keine Rechnungen im gewählten Zeitraum');
        return;
      }

      const buchungen = rechnungenZuDatevBuchungen(rechnungen as Rechnung[]);
      const csv = generiereeDatevCSV(buchungen);

      // CSV herunterladen
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DATEV_Export_${zeitraumVon}_bis_${zeitraumBis}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      // Export in DB speichern
      await supabase.from('datev_exporte').insert({
        zeitraum_von: zeitraumVon,
        zeitraum_bis: zeitraumBis,
        business_unit: betriebFilter !== 'alle' ? betriebFilter : null,
        anzahl_buchungen: buchungen.length,
        erstellt_von: 'System',
      });

      // Rechnungen als exportiert markieren
      const ids = rechnungen.map(r => r.id);
      await supabase
        .from('rechnungen')
        .update({ datev_exportiert: true, datev_export_datum: new Date().toISOString() })
        .in('id', ids);

      toast.success(`${buchungen.length} Buchungen exportiert`);
      onClose();
    } catch (err: any) {
      toast.error(`Export-Fehler: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold">DATEV CSV Export</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Von</label>
              <input type="date" value={zeitraumVon} onChange={(e) => setZeitraumVon(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Bis</label>
              <input type="date" value={zeitraumBis} onChange={(e) => setZeitraumBis(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase">Betrieb</label>
            <select value={betriebFilter} onChange={(e) => setBetriebFilter(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
              <option value="alle">Alle Betriebe</option>
              {BETRIEB_LIST.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <div className="p-3 rounded-lg bg-gray-50 text-xs text-gray-500">
            <p className="font-semibold text-gray-700 mb-1">DATEV-Format</p>
            <p>Export im DATEV-kompatiblen CSV-Format für den Steuerberater. Enthält Buchungssätze mit Kontonummern, Belegnummern und Kostenstellen.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-100">Abbrechen</button>
          <button onClick={handleExport} disabled={exporting} className="px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-semibold hover:bg-[#7F1D1D] disabled:opacity-50">
            {exporting ? 'Exportieren...' : 'CSV exportieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hauptseite ──
// embedded:          versteckt Seiten-Header + View-Tabs (Rechnungen/Zahlungen/Honorar),
//                    damit die reine Rechnungs-Liste in der konsolidierten Buchhaltung
//                    eingebettet werden kann (Feature buchhaltung-ein-ort).
// lockBusinessUnit:  fixiert den Betrieb-Filter auf eine Firma und blendet das
//                    Betrieb-Dropdown aus (z.B. 'mietwagen' = LCS, 'gutachten' = SV Büro).
export default function BuchhaltungPage({
  embedded = false,
  lockBusinessUnit,
  disableErstellung = false,
}: { embedded?: boolean; lockBusinessUnit?: BusinessUnit; disableErstellung?: boolean } = {}) {
  const [betriebFilter, setBetriebFilter] = useState<string>(lockBusinessUnit ?? 'alle');
  const [statusFilter, setStatusFilter] = useState<string>('alle');
  const [monatFilter, setMonatFilter] = useState<string>('alle');
  const [quelleFilter, setQuelleFilter] = useState<string>('alle');
  const [suchbegriff, setSuchbegriff] = useState('');
  const [showNeueRechnung, setShowNeueRechnung] = useState(false);
  const [showDatevExport, setShowDatevExport] = useState(false);
  const [activeView, setActiveView] = useState<'rechnungen' | 'zahlungen' | 'honorar'>('rechnungen');
  // PDF-Ansehen Modal (Feature finanzen-cockpit-v2)
  const [pdfModal, setPdfModal] = useState<{ url: string | null; titel: string } | null>(null);
  // Rechnungs-Vorschau Slide-over (Klick auf Zeile öffnet PDF-Vorschau von rechts)
  const [vorschauRechnung, setVorschauRechnung] = useState<any | null>(null);
  // Paginierung (clientseitig über die gefilterte Liste)
  const [pageSize, setPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Im Embedded-Modus ist der Betrieb fix (lockBusinessUnit); ansonsten der Filter-State.
  const effektiverBetrieb = lockBusinessUnit ?? betriebFilter;

  const { data: rechnungen, loading, refetch } = useRechnungen(
    effektiverBetrieb !== 'alle' ? effektiverBetrieb as BusinessUnit : undefined
  );

  // Verfügbare Monate (YYYY-MM) aus den rechnungsdatum-Werten ableiten — absteigend.
  const monatsOptionen = useMemo(() => {
    if (!rechnungen) return [] as string[];
    const set = new Set<string>();
    for (const r of rechnungen) {
      if (r.rechnungsdatum) set.add(r.rechnungsdatum.slice(0, 7));
    }
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [rechnungen]);

  const filteredRechnungen = useMemo(() => {
    if (!rechnungen) return [];
    return rechnungen.filter(r => {
      // Monats-Filter (rechnungsdatum YYYY-MM)
      if (monatFilter !== 'alle') {
        if (!r.rechnungsdatum || r.rechnungsdatum.slice(0, 7) !== monatFilter) return false;
      }
      // Status-Filter inkl. abgeleitetem "Überfällig"
      if (statusFilter === 'offen') {
        if (!istOffen(r.status) || istUeberfaellig(r)) return false;
      } else if (statusFilter === 'bezahlt') {
        if (r.status !== 'bezahlt') return false;
      } else if (statusFilter === 'ueberfaellig') {
        if (!istUeberfaellig(r)) return false;
      }
      // Quelle-Filter (WinValue / Stellungnahme / Manuell)
      if (quelleFilter === 'stellungnahme') {
        if (r.quelle !== 'stellungnahme') return false;
      } else if (quelleFilter === 'winvalue') {
        if (!(r.winvalue_rechnung_id || r.winvalue_dossier_id)) return false;
      } else if (quelleFilter === 'manuell') {
        if (r.quelle || r.winvalue_rechnung_id || r.winvalue_dossier_id) return false;
      }
      if (suchbegriff) {
        const s = suchbegriff.toLowerCase();
        return (
          r.rechnungs_nr?.toLowerCase().includes(s) ||
          r.betreff?.toLowerCase().includes(s) ||
          r.empfaenger_name?.toLowerCase().includes(s) ||
          r.kennzeichen?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [rechnungen, statusFilter, monatFilter, quelleFilter, suchbegriff]);

  // Bei Änderung von Filter/Suche/Seitengröße/Betrieb zurück auf Seite 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, monatFilter, quelleFilter, suchbegriff, pageSize, effektiverBetrieb]);

  // Clientseitige Paginierung über die gefilterte Liste.
  const gesamtAnzahl = filteredRechnungen.length;
  const pageCount = Math.max(1, Math.ceil(gesamtAnzahl / pageSize));
  const sichereSeite = Math.min(currentPage, pageCount);
  const paginierteRechnungen = useMemo(() => {
    const start = (sichereSeite - 1) * pageSize;
    return filteredRechnungen.slice(start, start + pageSize);
  }, [filteredRechnungen, sichereSeite, pageSize]);
  const vonIndex = gesamtAnzahl === 0 ? 0 : (sichereSeite - 1) * pageSize + 1;
  const bisIndex = Math.min(sichereSeite * pageSize, gesamtAnzahl);

  // Kompakte Seitenzahlen (mit Auslassungen) für die Fußzeile.
  const seitenZahlen = useMemo<(number | 'ellipsis')[]>(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const pages: (number | 'ellipsis')[] = [1];
    const left = Math.max(2, sichereSeite - 1);
    const right = Math.min(pageCount - 1, sichereSeite + 1);
    if (left > 2) pages.push('ellipsis');
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < pageCount - 1) pages.push('ellipsis');
    pages.push(pageCount);
    return pages;
  }, [pageCount, sichereSeite]);

  const totals = useMemo(() => {
    if (!rechnungen) return { gesamt: 0, offen: 0, bezahlt: 0, ueberfaellig: 0 };
    return {
      gesamt: rechnungen.reduce((s, r) => s + (r.brutto_betrag || 0), 0),
      offen: rechnungen.filter(r => ['offen', 'erstellt', 'versendet', 'gesendet', 'teilbezahlt'].includes(r.status)).reduce((s, r) => s + (r.brutto_betrag || 0), 0),
      bezahlt: rechnungen.filter(r => r.status === 'bezahlt').reduce((s, r) => s + (r.brutto_betrag || 0), 0),
      ueberfaellig: rechnungen.filter(r => istUeberfaellig(r)).reduce((s, r) => s + (r.brutto_betrag || 0), 0),
    };
  }, [rechnungen]);

  const monatLabel = useCallback((ym: string): string => {
    const [jahr, monat] = ym.split('-');
    const d = new Date(Number(jahr), Number(monat) - 1, 1);
    if (Number.isNaN(d.getTime())) return ym;
    return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, []);

  const handlePDF = async (rechnung: any) => {
    try {
      // Betriebsdaten laden
      const { data: betrieb } = await supabase.from('betriebe').select('*').eq('typ', rechnung.business_unit).single();
      const { data: positionen } = await supabase.from('rechnungspositionen').select('*').eq('rechnung_id', rechnung.id).order('position_nr');

      if (!betrieb) {
        toast.error('Betriebsdaten nicht gefunden');
        return;
      }

      const html = generiereRechnungHTML(rechnung, positionen || [], betrieb as BetriebDB);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
      }
    } catch (err: any) {
      toast.error(`PDF-Fehler: ${err.message}`);
    }
  };

  // "Ansehen": hinterlegte PDF (pdf_url) im Modal anzeigen — Fallback auf Druck-HTML.
  const handleAnsehen = (rechnung: any) => {
    if (rechnung.pdf_url) {
      setPdfModal({ url: rechnung.pdf_url, titel: rechnung.rechnungs_nr || 'Rechnung' });
    } else {
      // Keine PDF hinterlegt → bestehende HTML-Generierung als Vorschau.
      handlePDF(rechnung);
    }
  };

  return (
    <div className={embedded ? 'space-y-4' : 'p-4 lg:p-6 space-y-4'}>
      {/* Header — im Embedded-Modus ausgeblendet (kommt von der Wrapper-Seite) */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#9B1C1C]" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Buchhaltung</h1>
              <p className="text-xs text-muted-foreground">Rechnungen, Zahlungen & DATEV-Export</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDatevExport(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">DATEV Export</span>
            </button>
            {!disableErstellung && (
              <button
                onClick={() => setShowNeueRechnung(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Neue Rechnung</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Embedded-Toolbar: schlanke Aktionen (Neue Rechnung / DATEV) statt Header */}
      {embedded && (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setShowDatevExport(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">DATEV Export</span>
          </button>
          {!disableErstellung && (
            <button
              onClick={() => setShowNeueRechnung(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Neue Rechnung</span>
            </button>
          )}
        </div>
      )}

      {/* View Tabs — im Embedded-Modus ausgeblendet (nur Rechnungs-Liste) */}
      {!embedded && (
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          {[
            { id: 'rechnungen' as const, label: 'Rechnungen', icon: FileText },
            { id: 'zahlungen' as const, label: 'Zahlungen', icon: CreditCard },
            { id: 'honorar' as const, label: 'Honorar-Rechner', icon: Calculator },
          ].map(v => (
            <button
              key={v.id}
              onClick={() => setActiveView(v.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeView === v.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <v.icon className="w-3.5 h-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Sticky Kopfbereich: KPIs + (in der Rechnungs-Liste) Such-/Filterleiste.
          Bleibt beim Scrollen oben, mit Hintergrund, damit Inhalt nicht durchscheint. */}
      <div className={`sticky top-0 z-30 -mx-4 lg:-mx-6 px-4 lg:px-6 ${embedded ? 'pt-2 pb-3' : 'py-3'} bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 space-y-3`}>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Gesamt</p>
            <p className="text-lg font-bold mt-1">{formatEuro(totals.gesamt)}</p>
            <p className="text-[10px] text-gray-400">{rechnungen?.length || 0} Rechnungen</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Offen</p>
            <p className="text-lg font-bold mt-1 text-amber-600">{formatEuro(totals.offen)}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Bezahlt</p>
            <p className="text-lg font-bold mt-1 text-emerald-600">{formatEuro(totals.bezahlt)}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Überfällig</p>
            <p className="text-lg font-bold mt-1 text-red-600">{formatEuro(totals.ueberfaellig)}</p>
          </div>
        </div>

        {(embedded || activeView === 'rechnungen') && (
          /* Such-/Filterleiste (bleibt sticky) */
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={suchbegriff}
                onChange={(e) => setSuchbegriff(e.target.value)}
                placeholder="Suche nach Nr., Betreff, Empfänger..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm"
              />
            </div>
            {/* Betrieb-Filter nur ohne lockBusinessUnit (Embedded ist auf eine Firma fixiert) */}
            {!lockBusinessUnit && (
              <select value={betriebFilter} onChange={(e) => setBetriebFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-border bg-card text-sm">
                <option value="alle">Alle Betriebe</option>
                {BETRIEB_LIST.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            {/* Monats-Filter (aus vorhandenen rechnungsdatum-Werten) */}
            <select value={monatFilter} onChange={(e) => setMonatFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-border bg-card text-sm">
              <option value="alle">Alle Monate</option>
              {monatsOptionen.map(m => <option key={m} value={m}>{monatLabel(m)}</option>)}
            </select>
            {/* Status-Filter inkl. abgeleitetem "Überfällig" */}
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-border bg-card text-sm">
              {STATUS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {/* Quelle-Filter (WinValue / Stellungnahme / Manuell) */}
            <select value={quelleFilter} onChange={(e) => setQuelleFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-border bg-card text-sm">
              <option value="alle">Alle Quellen</option>
              <option value="stellungnahme">Stellungnahmen</option>
              <option value="winvalue">WinValue</option>
              <option value="manuell">Manuell</option>
            </select>
          </div>
        )}
      </div>

      {(embedded || activeView === 'rechnungen') && (
        <>
          {/* Rechnungen Table */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Rechnungen werden geladen...</div>
            ) : filteredRechnungen.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Keine Rechnungen vorhanden</p>
                {disableErstellung ? (
                  <p className="mt-3 text-xs text-muted-foreground">Rechnungen werden aus WinValue synchronisiert.</p>
                ) : (
                  <button onClick={() => setShowNeueRechnung(true)} className="mt-3 text-xs text-[#9B1C1C] font-semibold hover:underline">
                    Erste Rechnung erstellen
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                <div className="hidden lg:grid grid-cols-[120px_1fr_120px_100px_100px_80px] gap-4 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30">
                  <span>Nr.</span>
                  <span>Betreff / Empfänger</span>
                  <span>Betrieb</span>
                  <span>Status</span>
                  <span className="text-right">Betrag</span>
                  <span className="text-right">Aktion</span>
                </div>
                {paginierteRechnungen.map((rechnung, idx) => {
                  const betrieb = BETRIEBE[rechnung.business_unit as BetriebId];
                  const ueberfaellig = istUeberfaellig(rechnung);
                  const anzeigeStatus = ueberfaellig && rechnung.status !== 'ueberfaellig' && rechnung.status !== 'mahnung'
                    ? 'ueberfaellig'
                    : rechnung.status;
                  return (
                    <div
                      key={rechnung.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setVorschauRechnung(rechnung)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVorschauRechnung(rechnung); } }}
                      className={`lg:grid lg:grid-cols-[120px_1fr_120px_100px_100px_80px] lg:gap-4 flex flex-col px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-semibold text-gray-500">{rechnung.rechnungs_nr}</span>
                        {rechnung.quelle === 'stellungnahme' && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Stellungnahme</span>
                        )}
                        {rechnung.datev_exportiert && (
                          <span className="text-[8px] font-medium px-1 py-0.5 rounded bg-blue-50 text-blue-500">DATEV</span>
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{rechnung.betreff}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {rechnung.empfaenger_name || (rechnung.kunden ? `${rechnung.kunden.nachname}, ${rechnung.kunden.vorname}` : '-')}
                          {rechnung.kennzeichen ? ` · ${rechnung.kennzeichen}` : ''}
                        </span>
                      </div>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded self-start bg-gray-100 text-gray-500">
                        {betrieb?.kuerzel || rechnung.business_unit}
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full self-start ${RSTATUS_COLORS[anzeigeStatus] || 'bg-gray-100 text-gray-500'}`}>
                        {RSTATUS_LABELS[anzeigeStatus] || anzeigeStatus}
                      </span>
                      <span className="text-sm font-bold lg:text-right">{formatEuro(rechnung.brutto_betrag)}</span>
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setVorschauRechnung(rechnung)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Vorschau">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handlePDF(rechnung)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Drucken / PDF">
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sticky Fußzeile: Paginierung (Seitengröße, Bereichsanzeige, Seitenzahlen) */}
          {!loading && gesamtAnzahl > 0 && (
            <div className="sticky bottom-0 z-30 -mx-4 lg:-mx-6 px-4 lg:px-6 py-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <span>Pro Seite</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="px-2 py-1 rounded-lg border border-border bg-card text-xs"
                    >
                      {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <span>
                    {vonIndex.toLocaleString('de-DE')}–{bisIndex.toLocaleString('de-DE')} von {gesamtAnzahl.toLocaleString('de-DE')}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={sichereSeite <= 1}
                    className="px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Zurück
                  </button>
                  {seitenZahlen.map((p, i) =>
                    p === 'ellipsis' ? (
                      <span key={`e${i}`} className="px-1.5 text-xs text-muted-foreground select-none">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`min-w-[2rem] px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          p === sichereSeite
                            ? 'bg-[#9B1C1C] text-white'
                            : 'border border-border hover:bg-muted'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
                    disabled={sichereSeite >= pageCount}
                    className="px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!embedded && activeView === 'zahlungen' && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-gray-400" />
            <h2 className="text-sm font-bold">Zahlungseingänge</h2>
          </div>
          <p className="text-sm text-gray-400">Zahlungseingänge werden automatisch mit Rechnungen abgeglichen. Manuelle Erfassung und Bankimport werden in der nächsten Version unterstützt.</p>
          <div className="mt-4 p-3 rounded-lg bg-gray-50 text-xs text-gray-500">
            <p className="font-semibold text-gray-600 mb-1">Geplante Features</p>
            <ul className="space-y-1">
              <li>Automatischer Bankabgleich (MT940/CAMT)</li>
              <li>Manuelle Zahlungserfassung</li>
              <li>Teilzahlungen & Kürzungen</li>
              <li>Mahnwesen (1., 2., 3. Mahnung)</li>
            </ul>
          </div>
        </div>
      )}

      {!embedded && activeView === 'honorar' && <HonorarRechner />}

      {/* Dialoge */}
      {!disableErstellung && (
        <NeueRechnungDialog open={showNeueRechnung} onClose={() => setShowNeueRechnung(false)} onCreated={refetch} />
      )}
      <DatevExportDialog open={showDatevExport} onClose={() => setShowDatevExport(false)} />
      <PdfViewerDialog open={!!pdfModal} url={pdfModal?.url ?? null} titel={pdfModal?.titel} onClose={() => setPdfModal(null)} />
      <RechnungVorschauPanel rechnung={vorschauRechnung} onClose={() => setVorschauRechnung(null)} onDrucken={handlePDF} />
    </div>
  );
}

// ── Honorar-Rechner (Gutachten) ──
function HonorarRechner() {
  const { data: tabelle } = useHonorartabelle();
  const [schadenshoehe, setSchadenshoehe] = useState(3000);
  const [fahrtstrecke, setFahrtstrecke] = useState(20);

  const honorar = useMemo(() => {
    if (!tabelle || tabelle.length === 0) return null;
    const eintrag = tabelle.find(h => schadenshoehe >= h.schadenshoehe_von && schadenshoehe < h.schadenshoehe_bis);
    if (!eintrag) return null;

    const fahrtkosten = fahrtstrecke * eintrag.fahrtkosten_km;
    const netto = eintrag.grundhonorar + eintrag.foto_pauschale + eintrag.schreibkosten + eintrag.porto_telefon + eintrag.restwert_ermittlung + eintrag.wertminderung + fahrtkosten;
    const mwst = netto * 0.19;

    return {
      grundhonorar: eintrag.grundhonorar,
      fotoPauschale: eintrag.foto_pauschale,
      schreibkosten: eintrag.schreibkosten,
      portoTelefon: eintrag.porto_telefon,
      restwertErmittlung: eintrag.restwert_ermittlung,
      wertminderung: eintrag.wertminderung,
      fahrtkosten,
      fahrtkostenKm: eintrag.fahrtkosten_km,
      netto,
      mwst,
      brutto: netto + mwst,
    };
  }, [tabelle, schadenshoehe, fahrtstrecke]);

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <Calculator className="w-5 h-5 text-gray-400" />
        <div>
          <h2 className="text-sm font-bold">Honorar-Rechner (BVSK)</h2>
          <p className="text-[10px] text-gray-400">Berechnung nach BVSK-Honorartabelle</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[10px] font-semibold text-gray-400 uppercase">Schadenshöhe (EUR)</label>
          <input
            type="number"
            value={schadenshoehe}
            onChange={(e) => setSchadenshoehe(parseFloat(e.target.value) || 0)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-gray-400 uppercase">Fahrtstrecke (km)</label>
          <input
            type="number"
            value={fahrtstrecke}
            onChange={(e) => setFahrtstrecke(parseFloat(e.target.value) || 0)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
          />
        </div>
      </div>

      {honorar ? (
        <div className="space-y-2">
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
            {[
              { label: 'Grundhonorar', value: honorar.grundhonorar },
              { label: 'Foto-Pauschale', value: honorar.fotoPauschale },
              { label: 'Schreibkosten', value: honorar.schreibkosten },
              { label: 'Porto/Telefon', value: honorar.portoTelefon },
              { label: 'Restwertermittlung', value: honorar.restwertErmittlung },
              { label: 'Wertminderung', value: honorar.wertminderung },
              { label: `Fahrtkosten (${fahrtstrecke} km × ${formatEuro(honorar.fahrtkostenKm)})`, value: honorar.fahrtkosten },
            ].map((row, idx) => (
              <div key={idx} className={`flex justify-between px-4 py-2 text-sm ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
                <span className="text-gray-600">{row.label}</span>
                <span className="font-medium">{formatEuro(row.value)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between px-4 py-2 text-sm border-t-2 border-gray-200">
            <span className="font-semibold">Netto</span>
            <span className="font-bold">{formatEuro(honorar.netto)}</span>
          </div>
          <div className="flex justify-between px-4 py-1 text-xs text-gray-500">
            <span>MwSt. 19%</span>
            <span>{formatEuro(honorar.mwst)}</span>
          </div>
          <div className="flex justify-between px-4 py-2 text-base bg-gray-50 rounded-lg">
            <span className="font-bold">Brutto</span>
            <span className="font-bold text-[#9B1C1C]">{formatEuro(honorar.brutto)}</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400">Keine Honorardaten für diese Schadenshöhe verfügbar</p>
      )}
    </div>
  );
}
